import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';
import DataTableCard from '@/components/DataTableCard';
import EmptyState from '@/components/EmptyState';
import api from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw } from 'lucide-react';


const actionColors = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-purple-100 text-purple-800',
};

const AuditLogs = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [entityFilter, setEntityFilter] = useState('all');

  const fetchLogs = useCallback(async () => {
    setFetching(true);
    try {
      const res = await api.get(`/audit-logs?limit=500`);
      setLogs(res.data);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
    } else if (user) {
      if (user.role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/');
        return;
      }
      fetchLogs();
    }
  }, [user, loading, navigate, fetchLogs]);

  const actionOptions = useMemo(
    () => Array.from(new Set(logs.map((l) => l.action).filter(Boolean))).sort(),
    [logs]
  );
  const entityOptions = useMemo(
    () => Array.from(new Set(logs.map((l) => l.entity_type).filter(Boolean))).sort(),
    [logs]
  );

  const visibleLogs = logs.filter(
    (l) =>
      (actionFilter === 'all' || l.action === actionFilter) &&
      (entityFilter === 'all' || l.entity_type === entityFilter)
  );

  if (!user || user.role !== 'admin') return null;

  return (
    <PageShell>
      <PageHeader
        title="Audit Log"
        icon={ShieldCheck}
        titleTestId="audit-title"
        actions={
          <Button variant="outline" onClick={fetchLogs} data-testid="refresh-logs">
            <RefreshCw size={16} /> Refresh
          </Button>
        }
      />
      <p className="text-muted-foreground -mt-4 mb-6">
        Record of authenticated changes across the system
      </p>

      <div className="flex flex-col sm:flex-row flex-wrap gap-3 mb-4">
          <select
            className="w-full sm:w-auto border rounded-md px-3 py-2 text-sm bg-background"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            data-testid="filter-action"
            aria-label="Filter by action"
          >
            <option value="all">All actions</option>
            {actionOptions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
          <select
            className="w-full sm:w-auto border rounded-md px-3 py-2 text-sm bg-background"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            data-testid="filter-entity"
            aria-label="Filter by entity"
          >
            <option value="all">All entities</option>
            {entityOptions.map((en) => (
              <option key={en} value={en}>{en}</option>
            ))}
          </select>
        </div>

        {fetching ? (
          <p className="text-muted-foreground text-sm">Loading audit log…</p>
        ) : visibleLogs.length === 0 ? (
          <EmptyState icon={ShieldCheck} message="No audit entries." />
        ) : (
          <DataTableCard>
              <table className="w-full text-sm" data-testid="audit-logs-table">
                <thead className="bg-muted/50 text-left">
                  <tr>
                    <th className="p-3 font-medium" scope="col">Time</th>
                    <th className="p-3 font-medium" scope="col">User</th>
                    <th className="p-3 font-medium" scope="col">Action</th>
                    <th className="p-3 font-medium" scope="col">Entity</th>
                    <th className="p-3 font-medium" scope="col">Request</th>
                    <th className="p-3 font-medium" scope="col">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLogs.map((l) => (
                    <tr key={l.id} className="border-t" data-testid={`audit-row-${l.id}`}>
                      <td className="p-3 whitespace-nowrap text-muted-foreground">
                        {l.timestamp ? new Date(l.timestamp).toLocaleString() : ''}
                      </td>
                      <td className="p-3">
                        <div>{l.user_email || 'unknown'}</div>
                        {l.user_role ? (
                          <div className="text-xs text-muted-foreground">{l.user_role}</div>
                        ) : null}
                      </td>
                      <td className="p-3">
                        <Badge className={actionColors[l.action] || 'bg-gray-100 text-gray-800'}>
                          {l.action || l.method}
                        </Badge>
                      </td>
                      <td className="p-3">
                        {l.entity_type}
                        {l.entity_id ? (
                          <span className="text-xs text-muted-foreground"> · {l.entity_id}</span>
                        ) : null}
                      </td>
                      <td className="p-3 font-mono text-xs text-muted-foreground">
                        {l.method} {l.path}
                      </td>
                      <td className="p-3">{l.status_code}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
          </DataTableCard>
        )}
    </PageShell>
  );
};

export default AuditLogs;
